import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { STUDY_LANDSCAPES } from "@/features/story/data/landscapes";
import { canAccessHubNavId } from "@/features/access/access-level";
import { useAccessLevel } from "@/features/access/AccessLevelContext";
import { HUB_NAV } from "../data/nav";
import { parseLandscapeSelect, type HubLanguage } from "../state/hub-scope";
import { HubScopeProvider, useHubScope } from "../state/HubScopeContext";
import { Footer } from "@/app/layout/Footer";
import { HubSearchSelect } from "./HubSearchSelect";
import { HubUpdatesPanel } from "./HubUpdatesPanel";

const NAV_ICONS: Record<string, string> = {
  accueil: "M3 11 12 4l9 7M5 10v9h14v-9",
  services: "M12 3v18M5 8h14M7 12h10M9 16h6",
  indicateurs: "M4 19V5m0 14h16M8 15l3-4 3 2 4-6",
  collecte: "M12 5v10m0 0-3-3m3 3 3-3M5 19h14",
  rapport: "M12 3v12m0 0-3-3m3 3 3-3M6 21h12M6 2h9l5 5v4",
  administration:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a7.4 7.4 0 0 0-.1-1l2-1.5-2-3.5-2.4 1a7.6 7.6 0 0 0-1.7-1L15 3h-4l-.2 2.5a7.6 7.6 0 0 0-1.7 1L6.7 6l-2 3.5 2 1.5a7.4 7.4 0 0 0 0 2l-2 1.5 2 3.5 2.4-1a7.6 7.6 0 0 0 1.7 1L11 21h4l.2-2.5a7.6 7.6 0 0 0 1.7-1l2.4 1 2-3.5-2-1.5c.1-.3.1-.7.1-1Z",
};

const LANDSCAPE_OPTIONS = STUDY_LANDSCAPES.map((p) => ({
  value: p.id,
  label: p.shortLabel.toUpperCase(),
}));

function HubTopbar({
  sidebarOpen,
  onToggleSidebar,
  updatesOpen,
  onToggleUpdates,
}: {
  readonly sidebarOpen: boolean;
  readonly onToggleSidebar: () => void;
  readonly updatesOpen: boolean;
  readonly onToggleUpdates: () => void;
}) {
  const { t } = useTranslation();
  const { landscapeId, setLandscapeId, language, setLanguage } = useHubScope();

  return (
    <header className="nbs-hub__topbar">
      <div className="nbs-hub__topbar-start">
        {!sidebarOpen ? (
          <img
            className="nbs-hub__logo nbs-hub__logo--topbar"
            src="/favicon.svg"
            alt=""
            width={28}
            height={28}
            aria-hidden="true"
          />
        ) : null}
        <button
          type="button"
          className="nbs-hub__icon-btn"
          data-testid="hub-sidebar-toggle"
          aria-label={sidebarOpen ? t("hub.closeSidebar") : t("hub.openSidebar")}
          aria-expanded={sidebarOpen}
          aria-controls="hub-sidebar"
          onClick={onToggleSidebar}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            aria-hidden="true"
          >
            {sidebarOpen ? (
              <>
                <path d="M4 7h16M4 12h16M4 17h16" />
              </>
            ) : (
              <>
                <path d="M4 7h16M4 12h10M4 17h16" />
              </>
            )}
          </svg>
        </button>
        <HubSearchSelect
          className="nbs-hub-search-select--landscape"
          testId="hub-landscape-select"
          ariaLabel={t("hub.landscapeAria")}
          value={landscapeId}
          options={LANDSCAPE_OPTIONS}
          onChange={(v) => {
            const next = parseLandscapeSelect(v);
            if (next) setLandscapeId(next);
          }}
        />
      </div>
      <div className="nbs-hub__topbar-meta">
        <button
          type="button"
          className={`nbs-hub__icon-btn${updatesOpen ? " nbs-hub__icon-btn--active" : ""}`}
          data-testid="hub-updates-toggle"
          aria-label={t("hub.notificationsAria")}
          aria-expanded={updatesOpen}
          aria-controls="hub-updates-title"
          onClick={onToggleUpdates}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <path d="M6 9a6 6 0 1 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9Z" />
            <path d="M10 20a2 2 0 0 0 4 0" />
          </svg>
        </button>
        <select
          className="nbs-hub__plain-select"
          data-testid="hub-language-select"
          aria-label={t("hub.languageAria")}
          value={language}
          onChange={(e) => setLanguage(e.target.value as HubLanguage)}
        >
          <option value="fr">FR</option>
          <option value="en">EN</option>
        </select>
      </div>
    </header>
  );
}

function HubChrome() {
  const { t } = useTranslation();
  const { level, openPrompt } = useAccessLevel();
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 821px)").matches : true,
  );
  const [updatesOpen, setUpdatesOpen] = useState(false);
  const visibleNav = HUB_NAV.filter((item) => canAccessHubNavId(level, item.id));

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 821px)");
    const onChange = () => setSidebarOpen(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <div
      className={`nbs-hub${sidebarOpen ? " nbs-hub--sidebar-open" : " nbs-hub--sidebar-closed"}`}
      data-testid="hub-shell"
      data-sidebar={sidebarOpen ? "open" : "closed"}
    >
      <aside
        id="hub-sidebar"
        className="nbs-hub__sidebar"
        aria-label={t("hub.navAria")}
        aria-hidden={!sidebarOpen}
        inert={!sidebarOpen ? true : undefined}
      >
        <div className="nbs-hub__brand">
          <img
            className="nbs-hub__logo"
            src="/favicon.svg"
            alt=""
            width={36}
            height={36}
            aria-hidden="true"
          />
          <div>
            <p className="nbs-hub__brand-title">{t("hub.brandTitle")}</p>
          </div>
        </div>
        <nav className="nbs-hub__nav">
          <ul>
            {visibleNav.map((item) => {
              const icon = (
                <svg
                  className="nbs-hub__nav-ico"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d={NAV_ICONS[item.id] ?? NAV_ICONS.accueil} />
                </svg>
              );
              if (item.href) {
                return (
                  <li key={item.id}>
                    <a
                      className="nbs-hub__nav-link"
                      href={item.href}
                      download={item.download}
                      data-testid="hub-nav-rapport-download"
                    >
                      {icon}
                      <span>{t(item.labelKey)}</span>
                    </a>
                  </li>
                );
              }
              return (
                <li key={item.id}>
                  <NavLink
                    to={item.path!}
                    {...(item.end ? { end: true } : {})}
                    className={({ isActive }) =>
                      `nbs-hub__nav-link${isActive ? " nbs-hub__nav-link--active" : ""}`
                    }
                  >
                    {icon}
                    <span>{t(item.labelKey)}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
        {/* The story entry is open to every profile, so the way back always shows. */}
        <Link className="nbs-hub__back" to="/">
          {t("hub.backToPlatform")}
        </Link>
        <div className="nbs-hub__profile">
          <button
            type="button"
            className="nbs-hub__profile-btn"
            data-testid="hub-change-access"
            onClick={openPrompt}
          >
            {t("nav.changeAccess")}
          </button>
        </div>
      </aside>

      <div className="nbs-hub__workspace">
        <HubTopbar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((open) => !open)}
          updatesOpen={updatesOpen}
          onToggleUpdates={() => setUpdatesOpen((open) => !open)}
        />
        <div className="nbs-hub__content">
          <Outlet />
          <Footer />
        </div>
      </div>

      <HubUpdatesPanel open={updatesOpen} onClose={() => setUpdatesOpen(false)} />
    </div>
  );
}

/**
 * Pixel-mock chrome for the Hub prototype — self-contained sidebar + workspace.
 * Does not alter AppShell / existing pages.
 */
export function HubShell() {
  return (
    <HubScopeProvider>
      <HubChrome />
    </HubScopeProvider>
  );
}
